/**
 * #10 購物車頁 — LIFF /cart
 *
 * items list + 數量增減 + 移除 + 小計 + 「去結帳」按鈕。
 * 空車狀態顯示「您的購物車是空的」+ 回商品頁按鈕。
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import StatusMark from "../components/StatusMark";
import { useCartStore } from "../store/cartStore";
import { useUIStore } from "../store/uiStore";


export default function Cart() {
  const navigate = useNavigate();
  const cart = useCartStore((s) => s.cart);
  const loading = useCartStore((s) => s.loading);
  const refresh = useCartStore((s) => s.refresh);
  const setQty = useCartStore((s) => s.setQty);
  const remove = useCartStore((s) => s.remove);
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading || cart === null) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }

  const items = cart.items;

  return (
    <div className="min-h-screen bg-cream pb-28">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">購物車</h1>
      </header>

      {items.length === 0 ? (
        <div className="px-5 mt-16 text-center space-y-4">
          <StatusMark icon={ShoppingBag} />
          <div className="text-muted">您的購物車目前為空</div>
          <button onClick={() => navigate("/products")} className="btn-primary inline-block">
            返回商品頁
          </button>
        </div>
      ) : (
        <>
          <div className="px-5 mt-5 space-y-3">
            {items.map((it) => (
              <div key={it.product_id + it.mode} className="card space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-bold text-ink">{it.name}</div>
                    <div className="text-xs text-muted mt-0.5">{it.spec}</div>
                    <div className="text-sm text-ink mt-1">NT$ {it.price_each.toLocaleString()}</div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await remove(it.product_id);
                        showToast("已移除", "info");
                      } catch {
                        showToast("移除失敗", "error");
                      }
                    }}
                    aria-label={`移除 ${it.name}`}
                    className="text-muted text-sm hover:text-coral"
                  >
                    移除
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <QtyStepper
                    value={it.qty}
                    onChange={async (q) => {
                      try {
                        await setQty(it.product_id, q);
                      } catch {
                        showToast("更新失敗", "error");
                      }
                    }}
                  />
                  <div className="font-bold text-ink">NT$ {it.subtotal.toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>

          {/* 底部固定:小計 + 結帳按鈕 */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-line px-5 py-4 space-y-3 shadow-lg">
            <div className="flex items-center justify-between">
              <span className="text-muted">小計({cart.items_count} 件)</span>
              <span className="text-xl font-bold text-ink">NT$ {cart.total.toLocaleString()}</span>
            </div>
            <button onClick={() => navigate("/checkout")} className="btn-primary w-full">
              前往結帳
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function QtyStepper({ value, onChange }: { value: number; onChange: (q: number) => void | Promise<void> }) {
  return (
    <div className="flex items-center border border-line rounded-lg overflow-hidden">
      <button
        onClick={() => value > 1 && onChange(value - 1)}
        aria-label="減少數量"
        disabled={value <= 1}
        className="w-9 h-9 text-lg text-ink disabled:text-muted disabled:cursor-not-allowed"
      >
        −
      </button>
      <span className="w-10 text-center text-sm font-medium">{value}</span>
      <button
        onClick={() => onChange(value + 1)}
        aria-label="增加數量"
        className="w-9 h-9 text-lg text-ink"
      >
        ＋
      </button>
    </div>
  );
}
